import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { useLang } from "@/contexts/LangContext";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Clock } from "lucide-react";

export default function Consultations() {
  const { lang, t, pick } = useLang();
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    preferred_date: "",
    notes: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const location = useLocation();

  useEffect(() => {
    api.get("/services").then(({ data }) => setServices(data));
    if (new URLSearchParams(location.search).get("canceled") === "1") {
      toast.error(t("consult.canceled"));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!selected) return;
    setSubmitting(true);
    try {
      const { data } = await api.post("/bookings/checkout", {
        service_slug: selected.slug,
        ...form,
        origin_url: window.location.origin,
      });
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="page-consultations" className="max-w-[1440px] mx-auto px-6 sm:px-12 lg:px-24 pb-32">
      <section className="pt-20 pb-16">
        <p className="text-eyebrow text-brand mb-6">{t("consult.eyebrow")}</p>
        <h1 className="font-display font-light text-5xl sm:text-6xl md:text-7xl text-ink leading-none tracking-tighter">
          {t("consult.title")}
        </h1>
        <p className="mt-8 text-lg text-ink-muted font-light leading-relaxed max-w-2xl">
          {t("consult.subtitle")}
        </p>
      </section>

      {!selected ? (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {services.map((s, i) => (
            <article
              key={s.slug}
              data-testid={`service-${s.slug}`}
              className="border border-edge bg-bg-surface hover:border-brand transition-colors flex flex-col"
            >
              <div className="relative h-[240px] overflow-hidden">
                <img src={s.image} alt="" className="w-full h-full object-cover opacity-80" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#131211] via-transparent to-transparent" />
                <div className="absolute top-4 left-4 text-eyebrow text-brand">
                  {String(i + 1).padStart(2, "0")}
                </div>
              </div>
              <div className="p-7 flex-1 flex flex-col">
                <h3 className="font-display text-3xl text-ink mb-4">{pick(s, "title")}</h3>
                <p className="text-sm text-ink-muted leading-relaxed flex-1">
                  {pick(s, "description")}
                </p>
                <div className="mt-6 pt-6 border-t border-edge flex items-center justify-between">
                  <div>
                    <p className="text-eyebrow text-ink-dim">{t("consult.duration")}</p>
                    <p className="text-ink flex items-center gap-2 mt-1">
                      <Clock size={14} /> {s.duration_minutes} min
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="text-eyebrow text-ink-dim">{t("consult.price")}</p>
                    <p className="font-display text-3xl text-brand mt-1">
                      £{s.price.toFixed(0)}
                    </p>
                  </div>
                </div>
                <button
                  data-testid={`book-${s.slug}`}
                  onClick={() => setSelected(s)}
                  className="mt-6 w-full py-4 bg-brand text-ink-inverse text-sm font-medium tracking-wide hover:bg-brand-hover transition-colors"
                >
                  {t("consult.book")}
                </button>
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12">
          <div className="md:col-span-5">
            <button
              data-testid="back-to-services"
              onClick={() => setSelected(null)}
              className="text-eyebrow text-ink-muted hover:text-brand transition-colors mb-8"
            >
              {t("consult.back")}
            </button>
            <p className="text-eyebrow text-brand mb-4">{selected.slug}</p>
            <h2 className="font-display text-4xl text-ink mb-4">{pick(selected, "title")}</h2>
            <p className="text-ink-muted leading-relaxed mb-8">{pick(selected, "description")}</p>
            <div className="border-t border-b border-edge py-6 flex items-center justify-between">
              <div>
                <p className="text-eyebrow text-ink-dim">{t("consult.duration")}</p>
                <p className="text-ink mt-1">{selected.duration_minutes} min</p>
              </div>
              <div className="text-right">
                <p className="text-eyebrow text-ink-dim">{t("consult.price")}</p>
                <p className="font-display text-4xl text-brand mt-1">
                  £{selected.price.toFixed(0)}
                </p>
              </div>
            </div>
          </div>

          <form
            data-testid="booking-form"
            onSubmit={onSubmit}
            className="md:col-span-7 bg-bg-surface border border-edge p-8 md:p-10 space-y-5"
          >
            <h3 className="font-display text-2xl text-ink mb-2">{t("consult.formTitle")}</h3>
            <Field
              testId="booking-name"
              label={t("consult.name")}
              value={form.name}
              onChange={(v) => setForm({ ...form, name: v })}
              required
            />
            <Field
              testId="booking-email"
              type="email"
              label={t("consult.email")}
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              required
            />
            <Field
              testId="booking-phone"
              label={t("consult.phone")}
              value={form.phone}
              onChange={(v) => setForm({ ...form, phone: v })}
            />
            <Field
              testId="booking-date"
              type="date"
              label={t("consult.date")}
              value={form.preferred_date}
              onChange={(v) => setForm({ ...form, preferred_date: v })}
            />
            <div>
              <label className="text-eyebrow text-ink-muted block mb-2">
                {t("consult.notes")}
              </label>
              <textarea
                data-testid="booking-notes"
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                rows={4}
                className="w-full bg-transparent border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand transition-colors"
              />
            </div>
            <button
              data-testid="booking-submit"
              type="submit"
              disabled={submitting}
              className="w-full py-4 bg-brand text-ink-inverse text-sm font-medium tracking-wide hover:bg-brand-hover transition-colors disabled:opacity-60"
            >
              {submitting
                ? lang === "en"
                  ? "Redirecting to Stripe…"
                  : "Redirigiendo a Stripe…"
                : `${t("consult.payNow")} · £${selected.price.toFixed(0)}`}
            </button>
            <p className="text-xs text-ink-dim mt-4 text-center">
              {lang === "en"
                ? "Secured by Stripe. You will be redirected to a Stripe Checkout page."
                : "Asegurado por Stripe. Serás redirigido a una página de Stripe Checkout."}
            </p>
          </form>
        </div>
      )}
    </div>
  );
}

function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <label className="text-eyebrow text-ink-muted block mb-2">{label}</label>
      <input
        data-testid={testId}
        type={type}
        value={value}
        required={required}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-transparent border border-edge px-4 py-3 text-ink focus:outline-none focus:border-brand transition-colors"
      />
    </div>
  );
}
